// Ajustes de exibição de uma tela: o botão (controles deslizantes) no canto
// superior direito do cartão da barra de filtros, que abre uma janela com um
// interruptor (liga/desliga) por opção. Liga/desliga o que a tela MOSTRA —
// não é filtro (não recorta dados) e por isso não volta com "Limpar Filtros".
// Começou no Dashboard (valores de todos os períodos nos gráficos); toda tela
// que ganhar uma opção assim usa este mesmo componente, no mesmo lugar.
//
// Crie UMA vez por tela (como o `multiSelect`) e encaixe a cada render com
// `comAjustes`: o mesmo elemento volta à barra nova, então a janela continua
// aberta e o foco continua no interruptor depois de mudar uma opção.

import { botao as criarBotao, el } from "../ui.ts";
import { icon } from "../icons.ts";

export interface OpcaoExibicao {
  rotulo: string;
  ligada(): boolean;
  aoMudar(ligada: boolean): void;
}

export interface AjustesExibicao {
  readonly element: HTMLElement;
}

let seq = 0;

export function ajustesExibicao(opcoes: readonly OpcaoExibicao[]): AjustesExibicao {
  const idPainel = `ajustes-painel-${++seq}`;
  const contagem = el("span", { class: "ajustes-contagem" });
  const botao = criarBotao("secundario", {
    class: "ajustes-botao", title: "Ajustes de exibição",
    "aria-haspopup": "dialog", "aria-controls": idPainel, "aria-expanded": "false",
  }, icon("ajustes", 18), contagem);

  const interruptores = opcoes.map((o) => {
    const id = `ajuste-${++seq}`;
    const sw = el("button", { type: "button", role: "switch", class: "interruptor", id },
      el("span", { class: "interruptor-bolinha" }));
    sw.addEventListener("click", () => {
      o.aoMudar(!o.ligada());
      mostrar();
      // a tela redesenha a barra (este elemento sai e volta): o foco fica aqui
      sw.focus();
    });
    return { o, sw, linha: el("div", { class: "ajustes-linha" }, el("label", { for: id }, o.rotulo), sw) };
  });
  const mostrar = (): void => {
    for (const { o, sw } of interruptores) sw.setAttribute("aria-checked", String(o.ligada()));
    const ligadas = opcoes.filter((o) => o.ligada()).length;
    contagem.textContent = String(ligadas);
    contagem.hidden = ligadas === 0;
    botao.setAttribute("aria-label", ligadas > 0 ? `Ajustes de exibição (${ligadas} ligada${ligadas > 1 ? "s" : ""})` : "Ajustes de exibição");
  };

  const painel = el("div", { class: "ajustes-painel", id: idPainel, role: "dialog", "aria-label": "Exibição" },
    el("p", { class: "ajustes-titulo" }, "Exibição"),
    ...interruptores.map((i) => i.linha));
  const caixa = el("div", { class: "ajustes" }, botao, painel);

  // clique fora e Esc fecham; só escutam enquanto aberta (e se a tela saiu
  // com a janela aberta, o próximo clique solta os ouvintes)
  const fora = (e: MouseEvent): void => {
    if (!caixa.isConnected || !caixa.contains(e.target as Node)) abrir(false);
  };
  const esc = (e: KeyboardEvent): void => {
    if (e.key !== "Escape") return;
    abrir(false);
    if (caixa.isConnected) botao.focus();
  };
  const abrir = (sim: boolean): void => {
    caixa.classList.toggle("ajustes-aberto", sim);
    botao.setAttribute("aria-expanded", String(sim));
    if (sim) {
      document.addEventListener("click", fora);
      document.addEventListener("keydown", esc);
      interruptores[0]?.sw.focus();
    } else {
      document.removeEventListener("click", fora);
      document.removeEventListener("keydown", esc);
    }
  };
  botao.addEventListener("click", (e) => {
    e.stopPropagation();
    abrir(!caixa.classList.contains("ajustes-aberto"));
  });

  mostrar();
  return { element: caixa };
}

/** Põe o botão de ajustes no canto superior direito do cartão da barra de filtros. */
export function comAjustes(barra: HTMLElement, ajustes: AjustesExibicao): HTMLElement {
  barra.classList.add("barra-com-ajustes");
  barra.append(ajustes.element);
  return barra;
}
